import { File as FileIcon, X } from 'lucide-react-native';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

import { AttachFileButton } from '../AttachFileButton';
import { SurgeonAttachmentsInfoBox } from '../SurgeonAttachmentsInfoBox';
import { HStack } from '../ui/hstack';

import { Box } from '@/components/ui/box';
import { Button, ButtonIcon } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FILE_CONSTRAINTS, SUPPORTED_FILE_TYPES_DISPLAY } from '@/constants/fileTypes';
import { InquiryDraftFormData } from '@/hooks/useInquiryForm';
import { segmentClient } from '@/util/analytics';
import { getNativeTextProps } from '@/util/getNativeTextProps';

export type InquiryFile = {
  name: string;
  uri: string;
  mimeType: string;
  size: number;
  s3Key?: string;
  file?: any;
};

interface FileUploadFieldProps {
  control: Control<InquiryDraftFormData>;
  errors: FieldErrors<InquiryDraftFormData>;
  isDisabled: boolean;
  isExpertSurgeon: boolean;
  triggerFilePicker?: boolean;
  onTriggerComplete?: () => void;
  supportTextClassName?: string;
}

function FileItem({ file, onRemove }: { file: InquiryFile; onRemove: () => void }) {
  return (
    <HStack className="items-center gap-1.5 rounded-md bg-background-50 px-1.5 py-1">
      <Icon as={FileIcon} size="xs" className="text-typography-900" />

      <Text className="flex-1 font-medium text-xs text-typography-900" {...getNativeTextProps()}>
        {file.name}
      </Text>

      <Button
        variant="link"
        size="sm"
        onPress={onRemove}
        accessibilityLabel={`Remove attachment ${file.name}`}
        className="p-0.5">
        <ButtonIcon as={X} size="sm" className="text-typography-600" />
      </Button>
    </HStack>
  );
}

/**
 * File Upload Field Component
 *
 * @overview
 * Allows users to upload files as attachments to their inquiry. Supports up to 3 files
 * with a size limit of 25 MB per file. Validates file types and provides error handling.
 *
 * @behavior
 * - File picker with type filtering via AttachFileButton component
 * - File size and count validation with user feedback
 * - Visual file list with individual remove functionality
 * - Error display for validation issues
 * - Analytics tracking for ATTACHMENTS_REMOVED events
 * - Inline layout displaying files and attach button in horizontal flow
 * - File items show name with truncation and remove button with hover states
 * - Supports inquiry form context with appropriate analytics metadata
 */
export function FileUploadField({
  control,
  errors,
  isDisabled,
  isExpertSurgeon,
  triggerFilePicker,
  onTriggerComplete,
  supportTextClassName = '',
}: FileUploadFieldProps) {
  return (
    <Controller
      name="files"
      control={control}
      render={({ field: { onChange, value = [] } }) => {
        const hasFiles = value.length > 0;

        return (
          <FormControl
            isInvalid={!!errors.files?.message}
            isDisabled={isDisabled}
            className={twJoin(!hasFiles && 'mb-4 web:mb-0')}>
            {isExpertSurgeon && (
              <Box className="mb-3">
                <SurgeonAttachmentsInfoBox />
              </Box>
            )}

            <HStack className="mb-2 flex-wrap items-start gap-1.5">
              <AttachFileButton
                onFileSelected={(file) => {
                  const newFile: InquiryFile = {
                    name: file.name,
                    uri: file.uri,
                    mimeType: file.mimeType,
                    size: file.size,
                    file: file.file,
                  };

                  onChange([...value, newFile]);
                }}
                files={value.map((f) => ({
                  name: f.name,
                  size: f.size,
                  mimeType: f.mimeType,
                  uri: f.uri,
                  file: f.file,
                }))}
                isDisabled={isDisabled}
                size="md"
                context="inquiry_form"
                dismissKeyboard
                triggerFilePicker={triggerFilePicker}
                onTriggerComplete={onTriggerComplete}
                isSurgeonInquiry={isExpertSurgeon}
                textClassName="font-normal text-sm pr-2"
              />

              {value.map((file, index) => (
                <FileItem
                  key={`${file.uri}-${index}`}
                  file={file}
                  onRemove={() => {
                    segmentClient.track(ANALYTICS_EVENTS.ATTACHMENTS_REMOVED, {
                      file_name: file.name,
                      file_size: file.size,
                      mime_type: file.mimeType,
                      is_video: file.mimeType.startsWith('video/'),
                      is_surgeon_inquiry: isExpertSurgeon,
                      remaining_file_count: value.length - 1,
                      context: 'inquiry_form',
                    });

                    const newFiles = value.filter((_, i) => i !== index);

                    onChange(newFiles);
                  }}
                />
              ))}
            </HStack>

            <Text
              className={twJoin(
                'font-normal text-xs text-typography-600',
                hasFiles && 'mb-3 web:mb-2',
                supportTextClassName
              )}>
              {SUPPORTED_FILE_TYPES_DISPLAY} • Up to {FILE_CONSTRAINTS.MAX_FILE_COUNT} files
            </Text>

            {errors.files?.message && (
              <FormControlError className="mt-2">
                <FormControlErrorIcon />
                <FormControlErrorText size="xs">{errors.files?.message}</FormControlErrorText>
              </FormControlError>
            )}
          </FormControl>
        );
      }}
    />
  );
}
